import { useCallback, useEffect, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../api/errors'
import { LoginPage } from './LoginPage'
import { SESSION_QUERY_KEY, fetchSession } from './session'
import { PortalContextValue } from './sessionContext'
import {
  clearSelectedCustomer,
  readSelectedCustomer,
  validSelection,
  writeSelectedCustomer,
} from './selectedCustomer'
import { AccountChooser } from './AccountChooser'

/**
 * Gates the portal on a live server session and on a chosen customer.
 *
 * A user may belong to more than one customer, so when there is a choice the
 * portal asks rather than assuming. Picking the first membership silently is
 * how somebody ends up creating a credential — or reading a balance — for the
 * wrong company without noticing.
 *
 * With exactly one membership there is nothing to ask, so it is selected
 * directly; the question only appears when it is a real question.
 *
 * The answer survives a reload. It used to live in React state alone, so a
 * refresh or a bookmarked deep link sent a multi-account user back to the
 * chooser every time — which made deep links useless to precisely the people
 * who need them. The id is remembered in `localStorage` and re-checked against
 * the memberships the server returned on this session: a hint, never an
 * authorization. See `./selectedCustomer`.
 */
export function PortalProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  // Read once at mount rather than on each render: this is a starting point,
  // and re-reading would let a write from another tab yank the account out
  // from under somebody mid-task.
  const [selected, setSelected] = useState<string | null>(() => readSelectedCustomer())

  const choose = useCallback(
    (customerId: string) => {
      setSelected((previous) => {
        // Everything cached belongs to the customer being left: applications,
        // wallet, requests. Dropping it is what stops the next screen showing
        // the previous account's data for a frame.
        //
        // Only when actually leaving one. The first pick of a session has
        // nothing customer-scoped cached yet, so resetting there would just
        // re-fetch the session to arrive at the same answer.
        if (previous !== null && previous !== customerId) {
          void queryClient.resetQueries()
        }
        return customerId
      })
      writeSelectedCustomer(customerId)
    },
    [queryClient],
  )

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchSession(signal),
    retry: false,
  })

  // Every hook runs before the first early return below. The session query has
  // four outcomes and three of them return early; a hook placed after them
  // would be called on some renders and not others, which React reports as
  // "rendered more hooks than during the previous render" at the moment the
  // session resolves.
  const memberships = query.data?.memberships ?? []

  // The stored id is usable only if the server still lists it among this
  // user's active memberships. One that was disabled or removed is absent from
  // that list, so a selection naming it stops being valid.
  const effective = validSelection(selected, memberships)

  // Erasing is a side effect, so it happens after the render that noticed it,
  // not during it. Without this the stale id would sit in storage forever,
  // re-checked and re-rejected on every load.
  //
  // The state is deliberately left alone. `effective` is already null, so the
  // chooser is what renders; putting the component through another render to
  // agree with itself would buy nothing, and setting state from an effect is
  // the shape that turns into a loop the first time somebody edits it.
  useEffect(() => {
    // Only once the session has actually answered: an empty membership list
    // while the query is still loading is not evidence that a selection is
    // stale, and clearing on it would erase a good one on every cold load.
    if (query.data && selected !== null && effective === null) {
      clearSelectedCustomer()
    }
  }, [query.data, selected, effective])

  if (query.isPending) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress aria-label="در حال بررسی نشست شما" />
      </Box>
    )
  }

  const unauthenticated =
    query.isError && query.error instanceof ApiError && [401, 403].includes(query.error.status)

  if (unauthenticated || !query.data) {
    return (
      <LoginPage
        onSignedIn={() => void queryClient.resetQueries()}
        error={query.isError && !unauthenticated ? query.error : null}
      />
    )
  }

  if (memberships.length === 0) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 480 }}>
          <Typography variant="h2" gutterBottom>
            دسترسی به هیچ حسابی ندارید
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ورود شما انجام شد، اما عضو هیچ حساب مشتری نیستید. از مدیر حساب خود بخواهید شما را اضافه
            کند.
          </Typography>
        </Paper>
      </Box>
    )
  }

  const membership =
    memberships.find((row) => row.customer.id === effective) ??
    (memberships.length === 1 ? memberships[0] : undefined)

  if (!membership) {
    return <AccountChooser memberships={memberships} onChoose={choose} />
  }

  return (
    <PortalContextValue.Provider
      value={{
        session: query.data,
        membership,
        selectCustomer: choose,
      }}
    >
      {children}
    </PortalContextValue.Provider>
  )
}
